import { Upload, Youtube, Image as ImageIcon, ArrowUp, Globe, ChevronRight, ChevronLeft } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { Attachment, AttachmentType, AttachmentStatus } from "@/types/attachments";
import AttachmentChip from "./AttachmentChip";
import DocumentSelectionModal from "@/components/document/DocumentSelectionModal";
import { YouTubeUrlModal } from "@/components/youtube/YouTubeUrlModal";
import { ImageSelectionModal } from "@/components/image/ImageSelectionModal";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { config } from "@/lib/config";
import TopicOnboardingModal from "./TopicOnboardingModal";
import { useCreateTopic } from "@/hooks/useCreateTopic";
import { useNavigate } from "react-router-dom";
interface TopicCreationWidgetProps {
  onTopicCreate?: (topicId: string) => void;
}
export const TopicCreationWidget = ({
  onTopicCreate
}: TopicCreationWidgetProps) => {
  const [inputValue, setInputValue] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [showLeftChevron, setShowLeftChevron] = useState(false);
  const [showRightChevron, setShowRightChevron] = useState(false);
  const attachmentsScrollRef = useRef<HTMLDivElement>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [useInternet, setUseInternet] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const {
    toast
  } = useToast();
  const {
    session
  } = useAuth();
  const { createTopic, isCreating } = useCreateTopic();
  const navigate = useNavigate();
  const actionCards = [{
    id: 'upload',
    label: 'Upload Files',
    icon: Upload
  }, {
    id: 'youtube',
    label: 'Youtube',
    icon: Youtube
  }, {
    id: 'images',
    label: 'Images',
    icon: ImageIcon
  }];

  // Check for scroll overflow
  useEffect(() => {
    const checkScroll = () => {
      if (attachmentsScrollRef.current) {
        const {
          scrollLeft,
          scrollWidth,
          clientWidth
        } = attachmentsScrollRef.current;
        setShowLeftChevron(scrollLeft > 0);
        setShowRightChevron(scrollLeft < scrollWidth - clientWidth - 1);
      }
    };
    checkScroll();
    const scrollEl = attachmentsScrollRef.current;
    scrollEl?.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      scrollEl?.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [attachments]);
  const handleActionClick = (actionId: string) => {
    switch (actionId) {
      case 'upload':
        setShowUploadModal(true);
        break;
      case 'youtube':
        setShowYoutubeModal(true);
        break;
      case 'images':
        setShowImageModal(true);
        break;
    }
  };

  // New handler for deferred topic creation flow - Upload Documents
  const handleUploadFlowDocumentSelected = async (files: File[]) => {
    const file = files[0]; // Single file mode
    
    if (!file) return;
    
    try {
      // Extract clean title from filename
      const title = file.name.replace(/\.pdf$/i, '').replace(/[_-]/g, ' ');
      
      console.log('📄 Creating topic for document upload:', title);
      
      // Create topic with skipNavigation
      const result = await createTopic(title, 5, [], { skipNavigation: true });
      
      console.log('✅ Topic created, navigating with pending upload');
      
      // Navigate with file in router state
      navigate(`/topic/${result.id}`, {
        state: {
          pendingUpload: {
            file: file,
            topicId: result.id,
            shouldOpenOptimistically: true
          }
        }
      });
      
      setShowUploadModal(false);
    } catch (error) {
      console.error('❌ Failed to create topic for upload:', error);
      toast({
        title: "Failed to create topic",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
    }
  };

  // New handler for deferred topic creation flow - YouTube
  const handleYoutubeFlowVideoSelected = async (videoId: string, title: string, embedUrl: string) => {
    try {
      console.log('🎥 Creating topic for YouTube video:', title);
      
      // Create topic with skipNavigation
      const result = await createTopic(title, 5, [], { skipNavigation: true });
      
      console.log('✅ Topic created, navigating with pending YouTube video');
      
      // Navigate with video data in router state
      navigate(`/topic/${result.id}`, {
        state: {
          pendingYouTubeVideo: {
            videoId,
            title,
            embedUrl,
            topicId: result.id,
            shouldSaveImmediately: true
          }
        }
      });
      
      setShowYoutubeModal(false);
    } catch (error) {
      console.error('❌ Failed to create topic for YouTube video:', error);
      toast({
        title: "Failed to create topic",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
    }
  };

  // New handler for deferred topic creation flow - Images
  const handleImageFlowSelected = async (images: Array<{
    id: string;
    file: File;
    blobUrl: string;
    name: string;
  }>) => {
    try {
      const title = images.length === 1 
        ? images[0].name.replace(/\.(jpg|jpeg|png|gif|webp)$/i, '')
        : `${images.length} Images`;
      
      console.log('🖼️ Creating topic for images:', title);
      
      // Create topic with skipNavigation
      const result = await createTopic(title, 5, [], { skipNavigation: true });
      
      console.log('✅ Topic created, navigating with pending images');
      
      // Navigate with images in router state
      navigate(`/topic/${result.id}`, {
        state: {
          pendingImages: {
            images: images,
            topicId: result.id,
            shouldAutoSave: true
          }
        }
      });
      
      setShowImageModal(false);
    } catch (error) {
      console.error('❌ Failed to create topic for images:', error);
      toast({
        title: "Failed to create topic",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
    }
  };

  const handleRemoveAttachment = (id: string) => {
    setAttachments(prev => prev.filter(att => att.id !== id));
  };
  const handlePdfAdd = async (files: File[]) => {
    if (!session?.access_token) {
      toast({
        title: "Authentication required",
        description: "Please sign in to upload materials.",
        variant: "destructive"
      });
      return;
    }
    const uploads: Attachment[] = [];
    for (const file of files) {
      const tempAttachment: Attachment = {
        id: `pdf-${Date.now()}-${Math.random()}`,
        type: AttachmentType.PDF,
        name: file.name,
        status: AttachmentStatus.UPLOADING,
        progress: 20,
        mimeType: file.type || "application/pdf",
        file
      };
      setAttachments(prev => [...prev, tempAttachment]);
      uploads.push(tempAttachment);
      const formData = new FormData();
      formData.append("type", "pdf");
      formData.append("file", file);
      try {
        const response = await fetch(`${config.BACKEND_API_URL}/api/outline-sessions/extract`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`
          },
          body: formData
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Extraction failed");
        }
        const result = await response.json();
        setAttachments(prev => prev.map(att => att.id === tempAttachment.id ? {
          ...att,
          status: AttachmentStatus.COMPLETE,
          progress: 100,
          extractedText: result.attachment?.extracted_text || "",
          summary: result.attachment?.summary,
          attachmentId: result.attachment?.id
        } : att));
      } catch (error) {
        const message = error instanceof Error ? error.message : "Extraction failed";
        setAttachments(prev => prev.map(att => att.id === tempAttachment.id ? {
          ...att,
          status: AttachmentStatus.ERROR,
          error: message
        } : att));
        toast({
          title: "Upload failed",
          description: `Failed to process ${file.name}: ${message}`,
          variant: "destructive"
        });
      }
    }
  };
  const handleYoutubeAdd = async (videoId: string, title: string, embedUrl: string) => {
    if (!session?.access_token) {
      toast({
        title: "Authentication required",
        description: "Please sign in to add materials.",
        variant: "destructive"
      });
      return;
    }
    const tempAttachment: Attachment = {
      id: `youtube-${Date.now()}`,
      type: AttachmentType.YOUTUBE,
      name: title,
      url: embedUrl,
      videoId: videoId,
      thumbnail: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
      status: AttachmentStatus.UPLOADING,
      progress: 30
    };
    setAttachments(prev => [...prev, tempAttachment]);
    try {
      const response = await fetch(`${config.BACKEND_API_URL}/api/outline-sessions/extract`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          type: "youtube",
          video_url: embedUrl.replace("https://www.youtube.com/embed/", "https://www.youtube.com/watch?v=")
        })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to analyze video");
      }
      const result = await response.json();
      setAttachments(prev => prev.map(att => att.id === tempAttachment.id ? {
        ...att,
        status: AttachmentStatus.COMPLETE,
        progress: 100,
        extractedText: result.attachment?.extracted_text || "",
        summary: result.attachment?.summary,
        attachmentId: result.attachment?.id
      } : att));
    } catch (error) {
      const message = error instanceof Error ? error.message : "Extraction failed";
      setAttachments(prev => prev.map(att => att.id === tempAttachment.id ? {
        ...att,
        status: AttachmentStatus.ERROR,
        error: message
      } : att));
      toast({
        title: "Processing failed",
        description: `Failed to analyze video: ${title}`,
        variant: "destructive"
      });
    }
  };
  const handleImageAdd = (images: Array<{
    id: string;
    file: File;
    blobUrl: string;
    name: string;
  }>) => {
    // Just add to state as pending, no conversion/upload yet
    const newAttachment: Attachment = {
      id: `images-${Date.now()}`,
      type: AttachmentType.IMAGE,
      name: images.length === 1 ? images[0].name : `${images.length} Images`,
      files: images.map(img => img.file),
      thumbnails: images.map(img => img.blobUrl),
      thumbnail: images[0].blobUrl,
      status: AttachmentStatus.PENDING,
      progress: 0
    };
    setAttachments(prev => [...prev, newAttachment]);
  };
  const handleGenerateCourse = async () => {
    if (!inputValue.trim() || attachments.some(att => att.status === AttachmentStatus.PENDING || att.status === AttachmentStatus.UPLOADING || att.status === AttachmentStatus.PROCESSING)) {
      toast({
        title: "Attachments still processing",
        description: "Please wait for all attachments to finish uploading before generating.",
        variant: "destructive"
      });
      return;
    }
    
    setShowOnboardingModal(true);
  };
  const handleFlowComplete = () => {
    setInputValue("");
    setAttachments([]);
    setShowOnboardingModal(false);
  };
  return <div className="w-full px-4 mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="font-heading font-medium text-slate-900 mb-2 text-3xl">
          Learn something new!
        </h1>
      </div>

      {/* Action Cards */}
      <div className="flex gap-4 mb-4 h-[80px] w-full">
        {actionCards.map(action => <button key={action.id} onClick={() => handleActionClick(action.id)} className="flex-1 bg-white border border-[#d4d4d4] rounded-2xl p-4 cursor-pointer transition-all duration-200 hover:shadow-sm flex flex-col items-start justify-center gap-2">
            <action.icon className="w-6 h-6 text-[#666666]" strokeWidth={2} />
            <span className="font-medium text-[14px] leading-none" style={{
          fontFamily: 'Lexend Deca',
          color: 'rgba(0,0,0,0.6)'
        }}>
              {action.label}
            </span>
          </button>)}
      </div>

      {/* Divider with "Or" */}
      <div className="flex items-center gap-4 mb-4 w-full">
        <div className="flex-1 h-[1px] bg-border" />
        <span className="font-heading text-muted-foreground italic text-sm">Or</span>
        <div className="flex-1 h-[1px] bg-border" />
      </div>

      {/* Input Area */}
      <div className="bg-white border border-[#d4d6da] rounded-2xl p-3 flex flex-col justify-between w-full">
        {/* Attachments Display */}
        {attachments.length > 0 && <div className="relative mb-3">
            <div ref={attachmentsScrollRef} className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth">
              {attachments.map(attachment => <AttachmentChip key={attachment.id} attachment={attachment} onRemove={handleRemoveAttachment} />)}
            </div>

            {/* Left fade overlay and scroll button */}
            {showLeftChevron && <>
                <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-white to-transparent pointer-events-none" />
                <button onClick={() => {
            if (attachmentsScrollRef.current) {
              attachmentsScrollRef.current.scrollBy({
                left: -200,
                behavior: 'smooth'
              });
            }
          }} className="absolute left-2 top-1/2 -translate-y-1/2 w-6 h-6 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors">
                  <ChevronLeft className="w-4 h-4 text-gray-600" />
                </button>
              </>}

            {/* Right fade overlay and scroll button */}
            {showRightChevron && <>
                <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-white to-transparent pointer-events-none" />
                <button onClick={() => {
            if (attachmentsScrollRef.current) {
              attachmentsScrollRef.current.scrollBy({
                left: 200,
                behavior: 'smooth'
              });
            }
          }} className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors">
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                </button>
              </>}
          </div>}

        <label className="text-xs text-muted-foreground mb-1.5 block">
          Describe your topic
        </label>
        <textarea value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder="e.g., Introduction to Python, Spanish grammar, Medieval history..." className="bg-transparent border-none outline-none resize-none text-black text-[15px] leading-normal h-[80px] placeholder:font-light" style={{
        fontFamily: 'Lexend Deca',
        color: inputValue ? '#000000' : 'rgba(0,0,0,0.2)'
      }} />

        {/* Bottom row with toggle and button */}
        <div className="flex justify-end items-center gap-2">
          {/* Use the web for sources toggle */}
          {/* <div className="flex items-center gap-1 py-1.5 px-2 border border-[rgba(0,0,0,0.05)] rounded-full">
            <button onClick={() => setUseInternet(!useInternet)} className="relative transition-colors" style={{
            width: '21.6px',
            height: '12px',
            borderRadius: '6px',
            backgroundColor: useInternet ? '#000000' : '#e5e5e5'
          }}>
              <div className="absolute top-1/2 transform -translate-y-1/2 bg-white transition-all" style={{
              width: '8px',
              height: '8px',
              borderRadius: '4.8px',
              left: useInternet ? 'calc(100% - 10px)' : '2px',
              boxShadow: '0px 1px 2px rgba(0,0,0,0.2)'
            }} />
            </button>

            <Globe style={{
            width: '16px',
            height: '16px',
            color: '#666666',
            strokeWidth: '1.2px'
          }} />

            <span className="cursor-pointer select-none" onClick={() => setUseInternet(!useInternet)} style={{
            fontFamily: 'Lexend Deca',
            fontSize: '12px',
            fontWeight: 500,
            color: '#000000'
          }}>
              Use the web for sources
            </span>
          </div> */}

          {/* Submit Button */}
          <button onClick={handleGenerateCourse} disabled={!inputValue.trim()} className="bg-black hover:bg-gray-800 text-white rounded-full w-[30px] h-[30px] flex items-center justify-center disabled:opacity-50 transition-colors">
            <ArrowUp className="w-5 h-5" strokeWidth={1.67} />
          </button>
        </div>
      </div>

      {/* Modals */}
      <DocumentSelectionModal 
        isOpen={showUploadModal} 
        onClose={() => setShowUploadModal(false)} 
        onDocumentsAdd={handleUploadFlowDocumentSelected}
        singleFileMode={true}
      />

      <YouTubeUrlModal isOpen={showYoutubeModal} onClose={() => setShowYoutubeModal(false)} onVideoAdd={handleYoutubeFlowVideoSelected} />

      <ImageSelectionModal isOpen={showImageModal} onClose={() => setShowImageModal(false)} onImagesAdd={handleImageFlowSelected} />

      {/* Lesson Creation Modal */}
      <TopicOnboardingModal 
        open={showOnboardingModal} 
        onOpenChange={setShowOnboardingModal}
        initialMessage={inputValue}
        onFlowComplete={handleFlowComplete}
      />
    </div>;
};